<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { formatDate } from "@core/helpers"
import { ServiceError } from "@core/exceptions"
import { useI18n } from "vue-i18n"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import type { ColumnConfig } from "@types"
import type { OptimTurpeTemplateView } from "@modules/courtage/types/optim-turpe"
import { useOptimTurpeStore } from "@modules/courtage/stores/optim-turpe.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Historique des synthèses",
            dateHeader: "Date",
            reportLabel: "PDF",
            deleteLabel: "Supprimer la synthèse",
            expansionDetails: "Détail de la synthèse",
            deleteConfirmHeader: "Confirmation",
            deleteConfirmMessage:
                "Supprimer définitivement cette synthèse ? Cette action est irréversible.",
            deleteConfirmAccept: "Oui, supprimer",
            deleteConfirmReject: "Annuler",
            deleteErrorSummary: "Suppression impossible",
            deleteErrorDetail:
                "Une erreur est survenue lors de la suppression. Veuillez réessayer.",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    analyses: OptimTurpeTemplateView[]
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

const emit = defineEmits<{
    "click:report": [analyseId: string]
}>()

// ================================
// COMPOSABLES
// ================================
const confirm = useConfirm()
const toast = useToast()
const optimTurpeStore = useOptimTurpeStore()

// ================================
// STATE
// ================================
/** Clés `_id` des lignes développées (PrimeVue `dataKey` + object mode). */
const expandedRows = ref<Record<string, boolean>>({})

/** Analyses en cours de suppression (évite double envoi). */
const deletingAnalyseIds = ref<Set<string>>(new Set())

// ================================
// COMPUTED
// ================================
/**
 * Colonnes du tableau (expansion + date + PDF).
 */
const getTableColumns = computed<ColumnConfig[]>(() => [
    {
        field: "_toggle",
        header: "",
        expander: true,
        style: { width: "3rem" },
    },
    {
        field: "displayDate",
        header: t("dateHeader"),
        sortable: false,
    },
    {
        field: "actions",
        header: "",
        sortable: false,
        style: { width: "7rem" },
    },
])

/**
 * Lignes enrichies pour BaseTable (filtre global sur la date affichée).
 */
const getTableData = computed(() => {
    const data = props.analyses.map((analyse) => ({
        ...analyse,
        _toggle: "",
        displayDate: formatDate(
            analyse.createdAt,
            "[Synthèse effectuée le ] DD/MM/YYYY [à] HH:mm"
        ),
        actions: analyse._id,
    }))
    // Retire la première analyse
    data.shift()

    return data
})

// ================================
// INTERACTIONS
// ================================
/**
 * Ouvre la confirmation puis supprime l’analyse (optimiste dans le store).
 */
function onClickDeleteAnalyse(analyseId: string): void {
    confirm.require({
        header: t("deleteConfirmHeader"),
        message: t("deleteConfirmMessage"),
        icon: "pi pi-exclamation-triangle",
        rejectLabel: t("deleteConfirmReject"),
        acceptProps: {
            icon: "pi pi-trash",
            label: t("deleteConfirmAccept"),
            severity: "danger",
        },
        rejectProps: {
            label: t("deleteConfirmReject"),
            severity: "secondary",
            outlined: true,
        },
        accept: () => {
            void onAcceptDeleteAnalyse(analyseId)
        },
        reject: () => {},
    })
}

/**
 * Suppression après acceptation du dialogue : mise à jour optimiste + rollback + toast si échec.
 */
async function onAcceptDeleteAnalyse(analyseId: string): Promise<void> {
    if (deletingAnalyseIds.value.has(analyseId)) {
        return
    }
    deletingAnalyseIds.value = new Set(deletingAnalyseIds.value).add(analyseId)
    try {
        await optimTurpeStore.deleteAnalyseOptimistic(analyseId)
    } catch (error) {
        const detail =
            error instanceof ServiceError
                ? error.message
                : t("deleteErrorDetail")
        toast.add({
            severity: "error",
            summary: t("deleteErrorSummary"),
            detail,
        })
    } finally {
        const next = new Set(deletingAnalyseIds.value)
        next.delete(analyseId)
        deletingAnalyseIds.value = next
    }
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <BaseTitledCard
        class="optim-turpe-history-table border border-surface-200 shadow-none"
    >
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL — tableau + confirmation -->
            <!-- ================================ -->
            <BaseTable
                data-key="_id"
                :columns="getTableColumns"
                :value="getTableData"
                v-model:expanded-rows="expandedRows"
                search
                paginator
                :rows="5"
                :rows-per-page-options="[5, 10, 20]"
            >
                <template #cell_actions="{ data }">
                    <div class="flex items-center justify-end gap-1">
                        <BaseButton
                            text
                            rounded
                            icon="pi pi-file-pdf"
                            :aria-label="t('reportLabel')"
                            @click="emit('click:report', data._id)"
                        />
                        <BaseButton
                            text
                            rounded
                            severity="danger"
                            icon="pi pi-trash"
                            :aria-label="t('deleteLabel')"
                            :disabled="deletingAnalyseIds.has(data._id)"
                            @click="onClickDeleteAnalyse(data._id)"
                        />
                    </div>
                </template>
                <template #expansion="{ data }">
                    <div>
                        <OptimTurpeResultContent
                            :analyse="data"
                            :show-report-download="false"
                            @click:report="emit('click:report', data._id)"
                        />
                    </div>
                </template>
            </BaseTable>
            <!-- Cible PrimeVue `useConfirm()` — sans ce composant, aucune modale ne s’affiche. -->
            <BaseConfirmDialog class="max-w-[30rem] w-full" />
        </template>
    </BaseTitledCard>
</template>
